import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Users, Clock, Check, X, Loader2, UserPlus } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

export default function TeamJoinRequests() {
  const { team, userRole, canManage, token } = useOutletContext()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => {
    if (team?.team_id && canManage) {
      fetchRequests()
    } else {
      setLoading(false)
    }
  }, [team?.team_id, canManage])

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/invite-codes/pending/${team.team_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })

      if (res.ok) {
        const data = await res.json()
        setRequests(data)
      }
    } catch (err) {
      console.error('Error fetching requests:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (requestId, action, role = null) => {
    setActionLoading(requestId)
    try {
      const body = { action }
      if (role) body.role = role

      const res = await fetch(`${API_URL}/api/invite-codes/pending/${requestId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Environment': 'production'
        },
        body: JSON.stringify(body)
      })

      if (res.ok) {
        // Refresh requests
        fetchRequests()
      } else {
        const error = await res.json()
        alert(error.detail || 'Failed to process request')
      }
    } catch (err) {
      console.error('Error processing request:', err)
      alert('An error occurred')
    } finally {
      setActionLoading(null)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  if (!canManage) {
    return (
      <div className="text-center py-12 text-zinc-400">
        <Users className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
        <p>You don't have permission to manage join requests</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="team-join-requests-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Join Requests</h1>
        <p className="text-zinc-400 mt-1">
          Manage requests from people wanting to join {team?.name}
        </p>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-8 text-center">
          <UserPlus className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">No pending requests</p>
          <p className="text-zinc-500 text-sm mt-1">
            When someone uses an invite code to request to join, their request will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pending-requests-list">
          {requests.map((request) => (
            <div
              key={request.request_id}
              className="bg-zinc-800 rounded-xl border border-zinc-700 p-4"
              data-testid={`pending-request-${request.request_id}`}
            >
              <div className="flex items-start gap-4">
                {/* User Avatar */}
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {request.user_picture ? (
                    <img src={request.user_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-semibold text-zinc-500">
                      {request.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-white">{request.user_name}</p>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400 capitalize">
                      {request.role?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400">{request.user_email}</p>
                  <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(request.requested_at)}
                  </div>
                  {request.message && (
                    <div className="mt-2 p-2 bg-zinc-900 rounded-lg">
                      <p className="text-sm text-zinc-300 italic">"{request.message}"</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleAction(request.request_id, 'reject')}
                    disabled={actionLoading === request.request_id}
                    className="p-2.5 rounded-lg border border-zinc-700 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 hover:border-red-200 transition-colors disabled:opacity-50"
                    title="Reject"
                    data-testid={`reject-${request.request_id}`}
                  >
                    {actionLoading === request.request_id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <X className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleAction(request.request_id, 'approve')}
                    disabled={actionLoading === request.request_id}
                    className="p-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                    title="Approve"
                    data-testid={`approve-${request.request_id}`}
                  >
                    {actionLoading === request.request_id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
